import React, { memo, useRef, useCallback, useLayoutEffect, useMemo, useEffect, useState } from 'react';
import DynamicChart from './DynamicChart';
import './DashboardComponentRenderer.css';
import { useEmergency } from '../contexts/EmergencyContext';
import { useGlobalVariables } from '../contexts/GlobalVariablesContext';
import { normalizeChartEvent, evaluateEventExpression } from '../utils/chartEventNormalizer';
import { useDashboardPopup } from '../contexts/DashboardPopupContext';
import ContextMenu from './ContextMenu';
import OnRenderComplete from './common/OnRenderComplete';
import { showConfirmMessage, showConfirmPrompt, showSuccessMessage, showErrorMessage } from '../utils/dashboardMessageUtils';
import axios from 'axios';

/**
 * 대시보드 컴포넌트 렌더러
 * 개별 차트/컴포넌트의 렌더링 로직을 담당합니다.
 */
const DashboardComponentRenderer = memo(({ 
  component,
  dashboardTemplate,
  componentData,
  editMode,
  isDragging,
  draggedComponentId,
  resizeCounter,
  onComponentDoubleClick,
  onDeleteComponent,
  onRefreshChartData,
  onRefreshAllCharts,
  textLabelPreviewData,
  onChartClick,
  isActiveChart,
  isClicked,
  activeQuery,
  isSelected,
  onComponentSelect,
  onComponentClick,
  onZOrderAction,
  showContextMenu,
  contextMenuX,
  contextMenuY,
  zOrderMenuItems,
  closeContextMenu,
  onChartContextMenu,
  printMode = false,
  currentPopupId = null
}) => {
  // 모든 Hook을 함수 시작 직후에 선언
  const { triggerEmergency } = useEmergency();
  const globalVariables = useGlobalVariables();
  const { publishVariables, subscribeToVariables, getVariables, getVariable, evaluateExpression, debugMode, notifyLoadingComplete, isComponentLoading } = globalVariables;
  const { openDashboard } = useDashboardPopup();
  const clickTimerRef = useRef(null);
  const [builtWebframeUrl, setBuiltWebframeUrl] = useState(null); // 웹프레임 최종 URL 상태
  const [missingVariables, setMissingVariables] = useState([]); // 선택되지 않은 변수 목록
  const clickCountRef = useRef(0);
  const lastClickTimeRef = useRef(0);
  // 더블클릭 캡처용 레퍼런스 (네이티브 dblclick 누락 보완)
  const dcLastDownTsRef = useRef(0);
  const dcLastDownPosRef = useRef({ x: 0, y: 0 });
  
  // showContextMenu prop 로그 추가
  // 라인차트 렌더링 추적
  if (component?.chart_options?.viz_type === 'line') {
  }
  
  // showDescriptionTooltip 옵션을 확인하는 헬퍼 함수
  const shouldShowDescriptionTooltip = useCallback((component) => {
    // 올바른 위치만 확인 (options.advanced.showDescriptionTooltip)
    const value = component?.chart_options?.options?.advanced?.showDescriptionTooltip;
    // undefined나 true일 때는 표시, false일 때만 숨김
    return value !== false;
  }, []);

  // 마지막 갱신 시간을 포맷팅하는 헬퍼 함수
  const formatLastUpdated = useCallback((lastUpdated) => {
    if (!lastUpdated) return null;
    try {
      const date = new Date(lastUpdated);
      if (isNaN(date.getTime())) return null;
      return date.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch {
      return null;
    }
  }, []);

  // 정보 아이콘 툴팁 텍스트 생성 함수 (description + 마지막 갱신 시간)
  const getInfoTooltipText = useCallback((description, lastUpdated) => {
    const formattedTime = formatLastUpdated(lastUpdated);
    const parts = [];
    if (description) parts.push(description);
    if (formattedTime) parts.push(`마지막 갱신: ${formattedTime}`);
    return parts.join('\n\n');
  }, [formatLastUpdated]);

  // 정보 아이콘 표시 여부 (description이 있거나 lastUpdated가 있고, showDescriptionTooltip이 true일 때)
  const shouldShowInfoIcon = useCallback((component, lastUpdated) => {
    if (!shouldShowDescriptionTooltip(component)) return false;
    return !!(component.description || lastUpdated);
  }, [shouldShowDescriptionTooltip]);

  // 제목에서 placeholder를 변수 값으로 치환하는 함수
  const processTitleWithVariables = useCallback((title) => {
    if (!title || typeof title !== 'string') return title;
    
    // subscribed_variables가 없으면 원본 반환
    if (!component.subscribed_variables) return title;
    
    let processedTitle = title;
    
    // 각 구독 변수에 대해 placeholder 치환
    Object.entries(component.subscribed_variables).forEach(([varName, config]) => {
      const placeholder = `:${varName}`;
      if (processedTitle.includes(placeholder)) {
        // getVariables를 사용하여 현재 변수 값 가져오기
        const currentValue = getVariables({ [varName]: config })[varName];
        processedTitle = processedTitle.replace(new RegExp(placeholder, 'g'), currentValue || config.default || '');
      }
    });
    
    return processedTitle;
  }, [component.subscribed_variables, getVariables]);

  // 컨텐츠에서 placeholder를 변수 값으로 치환하는 함수 (text_label용)
  // 이제 백엔드에서 파라미터 바인딩을 처리하므로 이 함수는 불필요하지만 호환성을 위해 유지
  const processContentWithVariables = useCallback((content) => {
    // 백엔드에서 이미 처리되므로 원본 그대로 반환
    return content;
  }, []);

  
  // 이제 필요없는 핸들러들 - 빈 함수로만 유지
  const handleMouseMove = (e) => {};
  const handleMouseUp = (e) => {};
  
  // 편집 모드 mousedown 핸들러 - 드래그 시작 감지용
  const handleMouseDown = (e) => {
    if (!editMode) return;
    if (e.button === 2) return; // 우클릭 제외

    // mouseDown 시점 저장
    window.__mouseDownTime = Date.now();
    window.__mouseDownComponent = component.id;
    
    if (onComponentSelect) {
      onComponentSelect(component.id, e);
    }
  };
  
  // 클릭 핸들러 (드래그 없이 클릭만 했을 때)
  const handleClick = (e) => {
    // 디버깅: 컨테이너 레벨 클릭 감지
    console.log('[DashboardComponentRenderer] Container click', {
      componentId: component.id,
      editMode,
      target: e.target?.tagName,
      className: e.target?.className?.substring?.(0, 50)
    });
    if (!editMode) return;
    if (e.button === 2) return;
    
    // 드래그가 발생했는지 확인
    const timeSinceMouseDown = Date.now() - (window.__mouseDownTime || 0);
    const wasDrag = window.__wasDragging || timeSinceMouseDown > 200;
    
    if (!wasDrag && onComponentClick) {
      console.log('[DashboardComponentRenderer] 클릭 감지:', component.id);
      onComponentClick(component.id, e);
    }
    
    // 플래그 초기화
    window.__wasDragging = false;
  };

  // 더블클릭 캡처 핸들러: 자식(ECharts 등)에서 이벤트를 가로채더라도 편집 모달이 열리도록 캡처 단계에서 처리
  const handleDoubleClickCapture = useCallback((e) => {
    if (!editMode) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') {
      e.nativeEvent.stopImmediatePropagation();
    }
    onComponentDoubleClick(component);
  }, [editMode, onComponentDoubleClick, component]);

  // 네이티브 dblclick이 누락되는 환경(드래그 임계치 개입 등)을 보완하기 위한 캡처 단계 mousedown 기반 감지
  const handleMouseDownCapture = useCallback((e) => {
    if (!editMode) return;
    if (e.button === 2) return; // 우클릭 제외

    const now = Date.now();
    const prev = dcLastDownTsRef.current || 0;
    const dt = now - prev;
    const x = e.clientX;
    const y = e.clientY;
    const dx = Math.abs(x - (dcLastDownPosRef.current.x || 0));
    const dy = Math.abs(y - (dcLastDownPosRef.current.y || 0));
    const distanceOk = dx <= 6 && dy <= 6;

    // 350ms 이내, 이동 거의 없음 -> 더블클릭으로 간주
    if (prev > 0 && dt <= 350 && distanceOk) {
      e.preventDefault();
      e.stopPropagation();
      if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') {
        e.nativeEvent.stopImmediatePropagation();
      }
      // 즉시 모달 오픈
      onComponentDoubleClick(component);
      // 리셋
      dcLastDownTsRef.current = 0;
      dcLastDownPosRef.current = { x: 0, y: 0 };
      return;
    }

    // 1회 클릭 기록
    dcLastDownTsRef.current = now;
    dcLastDownPosRef.current = { x, y };
  }, [editMode, onComponentDoubleClick, component]);
  
  // 프레임 표시 여부 먼저 계산 (chartProps에서 사용하기 위함)
  const isBadgeChart = component?.chart_options?.viz_type?.toLowerCase() === 'badge' || component?.type?.toLowerCase() === 'badge';
  const isWebframeChart = component?.chart_options?.viz_type?.toLowerCase() === 'webframe' || component?.type?.toLowerCase() === 'webframe';
  const isBatteryGridChart = component?.chart_options?.viz_type?.toLowerCase() === 'battery_grid' || component?.type?.toLowerCase() === 'battery_grid';
  const isDatePickerChart = component?.viz_type?.toLowerCase() === 'date_picker' || component?.chart_options?.viz_type?.toLowerCase() === 'date_picker' || component?.type?.toLowerCase() === 'date_picker';
  const defaultShowFrame = !isBadgeChart; // BADGE는 false, 나머지는 true
  const originalShowFrame = component?.chart_options?.showFrame !== undefined ? component.chart_options.showFrame : defaultShowFrame;
  // 투명 옵션은 chart_options.transparent로 승격 (하위호환 제거)
  // transparent가 명시적으로 설정된 경우 그 값을 사용 (true/false/"true"/"false" 모두 처리)
  const transparentOption = component?.chart_options?.transparent;

  // 모서리 둥글기 옵션 (기본값: 24px) - 다양한 경로 확인
  const borderRadiusOption = (() => {
    const options = component?.chart_options?.options;
    // 1. style.background.borderRadius (표준)
    if (options?.style?.background?.borderRadius !== undefined) {
      return options.style.background.borderRadius;
    }
    // 2. style.borderRadius (레거시 호환)
    if (options?.style?.borderRadius !== undefined) {
      return options.style.borderRadius;
    }
    // 3. advanced.borderRadius (일부 차트 타입 호환)
    if (options?.advanced?.borderRadius !== undefined) {
      return options.advanced.borderRadius;
    }
    return undefined;
  })();
  
  // chartProps를 미리 메모이제이션 (조건문 전에 Hook 호출)
  const chartProps = useMemo(() => {
    if (!componentData || !componentData.chart_data) {
      return null;
    }
    
    // webframe 타입인 경우 특별한 로그 추가
    if (componentData.chart_data?.chart_options?.viz_type === 'webframe') {
    }
    
    // 기본 옵션 구성
    // 바깥쪽 title을 우선적으로 사용 (placeholder 처리 포함)
    const processedTitle = processTitleWithVariables(
      component.title || componentData.chart_data?.chart_options?.title
    );
    
    // 원본 options에서 transparent 키는 제거(승격 정책: chart_options.transparent만 사용)
    const originalOptions = componentData.chart_data?.chart_options?.options || {};
    const { transparent: _ignoredTransparent, ...optionsSansTransparent } = originalOptions;

    // visibility.title을 showTitle로 매핑
    const showTitle = originalOptions?.style?.visibility?.title !== false;
    
    const baseOptions = {
      ...optionsSansTransparent,
      title: processedTitle,
      showTitle: showTitle,  // visibility.title 매핑 추가 (아래 병합에서 재계산 가능)
      showFrame: originalShowFrame,
      // 투명 옵션을 하위 렌더러가 쉽게 참조하도록 options에도 동기화
      // transparentOption이 명시적으로 설정된 경우 해당 값 전달
      transparent: transparentOption
    };
    
    // 배터리 그리드의 경우 템플릿(클라이언트) 옵션을 서버 응답 옵션 위에 병합하여
    // 제목 표시 토글 등 즉시 반영되도록 처리
    const currentVizType = componentData.chart_data?.chart_options?.viz_type || component.chart_options?.viz_type || component.type;
    let effectiveOptions = baseOptions;
    if ((currentVizType || '').toLowerCase() === 'battery_grid') {
      const clientOptions = component.chart_options?.options || {};
      const serverStyle = optionsSansTransparent?.style || {};
      const clientStyle = clientOptions?.style || {};
      const mergedStyle = {
        ...serverStyle,
        ...clientStyle,
        // visibility는 템플릿 우선 적용 (서버 응답에 누락되거나 갱신 지연되는 경우 대비)
        ...(clientStyle?.visibility ? { visibility: clientStyle.visibility } : (serverStyle?.visibility ? { visibility: serverStyle.visibility } : {}))
      };
      // battery_grid에도 advanced 병합을 동일 정책으로 적용 (client 우선)
      const serverAdvanced = optionsSansTransparent?.advanced || {};
      const clientAdvanced = clientOptions?.advanced || {};
      // 루트 옵션(gridColumns/gridRows/cellType)이 존재하면 advanced로 승격하여 정규화
      const rootGridColumns = clientOptions?.gridColumns;
      const rootGridRows = clientOptions?.gridRows;
      const rootCellType = clientOptions?.cellType;
      const mergedAdvanced = {
        ...serverAdvanced,
        ...clientAdvanced,
        ...(rootGridColumns !== undefined ? { gridColumns: rootGridColumns } : {}),
        ...(rootGridRows !== undefined ? { gridRows: rootGridRows } : {}),
        ...(rootCellType !== undefined ? { cellType: rootCellType } : {})
      };
      const mergedOptions = {
        ...optionsSansTransparent,
        ...clientOptions,
        style: mergedStyle,
        ...(Object.keys(mergedAdvanced).length ? { advanced: mergedAdvanced } : {})
      };
      // 정규화 후 루트의 레거시 키는 렌더 프롭에서 제거하여 하위 렌더러 혼선을 방지
      if (mergedOptions.gridColumns !== undefined) delete mergedOptions.gridColumns;
      if (mergedOptions.gridRows !== undefined) delete mergedOptions.gridRows;
      if (mergedOptions.cellType !== undefined) delete mergedOptions.cellType;
      const mergedShowTitle = mergedOptions?.style?.visibility?.title !== false;
      effectiveOptions = {
        ...mergedOptions,
        title: processedTitle,
        showTitle: mergedShowTitle,
        showFrame: originalShowFrame,
        transparent: transparentOption
      };
    } else {
      // 일반 ECharts 차트들(line, bar, scatter, heatmap, pie, radar 등)에 대해서도
      // 서버 옵션과 로컬 템플릿 옵션을 병합하여 즉시 반영되도록 처리
      const clientOptions = component.chart_options?.options || {};
      const serverStyle = optionsSansTransparent?.style || {};
      const clientStyle = clientOptions?.style || {};
      const mergedStyle = {
        ...serverStyle,
        ...clientStyle,
        ...(clientStyle?.visibility ? { visibility: clientStyle.visibility } : (serverStyle?.visibility ? { visibility: serverStyle.visibility } : {}))
      };
      // showDescriptionTooltip을 제외한 advanced 옵션 병합
      const { showDescriptionTooltip: serverShowDesc, ...serverAdvancedClean } = (optionsSansTransparent?.advanced || {});
      const { showDescriptionTooltip: clientShowDesc, ...clientAdvancedClean } = (clientOptions?.advanced || {});
      const mergedAdvanced = {
        ...serverAdvancedClean,
        ...clientAdvancedClean
      };
      const mergedAxes = clientOptions?.axes ? clientOptions.axes : optionsSansTransparent?.axes;
      const mergedOptions = {
        ...optionsSansTransparent,
        ...clientOptions,
        ...(mergedAxes ? { axes: mergedAxes } : {}),
        style: mergedStyle,
        advanced: mergedAdvanced
      };
      const mergedShowTitle = mergedOptions?.style?.visibility?.title !== false;
      effectiveOptions = {
        ...mergedOptions,
        title: processedTitle,
        showTitle: mergedShowTitle,
        showFrame: originalShowFrame,
        transparent: transparentOption
      };
    }
    
             // activeQuery에서 알람 조건 동기화 (소스 컴포넌트의 알람 조건 적용)
    if (activeQuery?.alertConditions) {
      // advanced 옵션에 알람 조건 덮어쓰기
      if (!baseOptions.advanced) baseOptions.advanced = {};
      
      // 소스 컴포넌트의 알람 조건을 현재 컴포넌트에 적용
      if (activeQuery.alertConditions.alertColumn) {
        baseOptions.advanced.alertColumn = activeQuery.alertConditions.alertColumn;
      }
      if (activeQuery.alertConditions.alertCondition) {
        baseOptions.advanced.alertCondition = activeQuery.alertConditions.alertCondition;
      }
      if (activeQuery.alertConditions.criticalCondition) {
        baseOptions.advanced.criticalCondition = activeQuery.alertConditions.criticalCondition;
      }
      if (activeQuery.alertConditions.emergencyCondition) {
        baseOptions.advanced.emergencyCondition = activeQuery.alertConditions.emergencyCondition;
      }
      
    }

    const props = {
      viz_type: componentData.chart_data?.chart_options?.viz_type,
      options: effectiveOptions,
      data: componentData.chart_data?.chart_options?.data || {}, // 표준화된 구조: chart_options.data
      lastUpdated: componentData.lastUpdated,
      skipUpdateAnimation: componentData.skipUpdateAnimation // 타이머 갱신 시 애니메이션 스킵
    };

    // 표준 데이터 보강: 서버가 raw_data만 보낼 때 파이 차트용 카테고리/시리즈 생성
    try {
      if ((props.viz_type || '').toLowerCase() === 'pie') {
        const hasSeries = Array.isArray(props.data?.series) && props.data.series.length > 0;
        const hasCategories = Array.isArray(props.data?.categories) && props.data.categories.length > 0;
        if (!(hasSeries || hasCategories)) {
          const raw = componentData.raw_data || componentData.query_result || componentData.chart_data?.chart_options?.data?.raw_data;
          if (Array.isArray(raw) && raw.length > 0) {
            const firstRow = raw[0];
            const keys = Object.keys(firstRow);
            if (keys.length >= 2) {
              const catKey = keys[0];
              const valKey = keys[1];
              const categories = raw.map(r => String(r[catKey]));
              const values = raw.map(r => Number(r[valKey]));
              props.data = {
                categories,
                series: [{ name: '데이터', values }]
              };
            }
          }
        }
      }
    } catch (e) {
      // 데이터 보강 실패 시 무시
    }
    
    // webframe의 경우 URL을 표준 위치에 추가
    if (props.viz_type === 'webframe') {
      // builtWebframeUrl 우선 사용 (SSO/쿼리파라미터 포함), 없으면 원본 URL
      props.url = builtWebframeUrl || 
                  component.data_source?.url || 
                  componentData.chart_data?.url ||
                  componentData.chart_data?.chart_options?.data?.url; // 레거시 지원
    }
    
    if (componentData.sampling_info) {
      props.sampling_info = componentData.sampling_info;
    }
    
    return props;
  }, [
    componentData?.chart_data?.chart_options?.viz_type,
    componentData?.chart_data?.chart_options?.options,
    componentData?.chart_data?.chart_options?.title,
    componentData?.chart_data?.chart_options?.data,
    componentData?.lastUpdated,
    componentData?.skipUpdateAnimation,
    componentData?.sampling_info,
    originalShowFrame,
    activeQuery?.alertConditions,
    component.title,  // 바깥쪽 title 추가
    processTitleWithVariables,  // placeholder 처리 함수 추가
    // 템플릿(로컬) 변경도 즉시 반영되도록 의존성에 포함
    component?.chart_options?.options,
    component?.chart_options?.transparent,
    component?.chart_options?.showFrame,
    builtWebframeUrl  // 웹프레임 URL 빌더 결과 추가
  ]);
  
  // 클릭 가능 여부 판단 (on_click 추가)
  const isClickable = component.on_click ||  // pub/sub용 새로운 클릭 핸들러
                     component.on_clicked ||
                     component.on_clicked_other ||
                     component.on_clicked_others ||
                     (dashboardTemplate?.components || []).some(c =>
                       (c.on_clicked_other && c.on_clicked_other[component.id]) ||
                       (c.on_clicked_others && c.on_clicked_others[component.id])
                     );
  // 런타임에서 실제 클릭 핸들러(on_click)가 있는 경우에만 커서/클래스 적용
  const isClickableRuntime = !editMode && (
    !!component.on_click?.publish ||
    !!component.on_click?.openDashboard ||
    !!component.on_click?.callFunction
  );
  
  // 콜백 함수들도 미리 메모이제이션
  const handleChartDoubleClick = useCallback(() => {
    onComponentDoubleClick(component);
  }, [onComponentDoubleClick, component]);
  
  // 웹프레임 URL에 사용되는 변수들의 현재 값을 추적 (변수 변경 시 재빌드 트리거)
  const webframeUrlVariableValues = useMemo(() => {
    const isWebframe = component?.chart_options?.viz_type?.toLowerCase() === 'webframe';
    if (!isWebframe) return null;
    
    const url = component?.data_source?.url || '';
    const matches = [...url.matchAll(/\{:(\w+)\}/g)];
    if (matches.length === 0) return '';
    
    // 각 변수의 현재 값을 조합하여 문자열로 반환 (변경 감지용)
    return matches.map(m => {
      const value = getVariable(m[1]);
      return `${m[1]}=${value ?? ''}`;
    }).join('|');
  }, [component?.chart_options?.viz_type, component?.data_source?.url, getVariable]);

  // 변수 구독 처리 (초기 추가 로드 없이, 변경 시에만 새로고침)
  // 웹프레임 URL 빌더 (SSO 토큰 + 쿼리 파라미터)
  useEffect(() => {
    const isWebframe = component?.chart_options?.viz_type?.toLowerCase() === 'webframe';
    if (!isWebframe) return;

    const dataSource = component?.data_source;
    if (!dataSource?.url) return;

    const buildWebframeUrl = async () => {
      try {
        // URL에서 변수 패턴 추출
        const variablePattern = /\{:(\w+)\}/g;
        const matches = [...(dataSource.url || '').matchAll(variablePattern)];
        
        // 모든 변수가 설정되었는지 확인
        const allVariablesSet = matches.length === 0 || matches.every(match => {
          const value = getVariable(match[1]);
          return value !== undefined && value !== null && value !== '';
        });
        
        // 변수가 하나라도 설정되지 않았으면 빌드 보류 (로딩 상태 유지)
        if (!allVariablesSet) {
          // 설정되지 않은 변수 목록 추출
          const unsetVars = matches
            .filter(match => {
              const value = getVariable(match[1]);
              return value === undefined || value === null || value === '';
            })
            .map(match => match[1]);
          
          console.log('[WebFrame URL Builder] 변수가 아직 설정되지 않음, 빌드 보류:', 
            matches.map(m => `${m[1]}=${getVariable(m[1])}`).join(', '));
          setMissingVariables(unsetVars);
          setBuiltWebframeUrl(null);
          return;
        }
        
        // 변수가 모두 설정되면 missingVariables 초기화
        setMissingVariables([]);
        
        // 공통 템플릿 치환 함수: {:변수명} 패턴을 변수값으로 치환
        const replaceVariables = (template) => {
          if (!template) return '';
          return template.replace(/\{:(\w+)\}/g, (match, varName) => {
            const value = getVariable(varName);
            return value !== undefined && value !== null ? String(value) : '';
          });
        };
        
        // 1. Path 변수 치환 (URL에 포함된 {:변수명} 패턴)
        let urlString = replaceVariables(dataSource.url);
        const url = new URL(urlString);
        
        // 2. 쿼리 파라미터 추가 (템플릿 치환)
        if (dataSource.query_params && Array.isArray(dataSource.query_params)) {
          dataSource.query_params.forEach(({ param_name, variable_name, default_value }) => {
            if (!param_name) return;
            
            // 템플릿 치환 (예: "golfcart_{:number}" → "golfcart_001")
            let value = replaceVariables(variable_name || '');
            
            // 값이 비어있으면 기본값 사용
            if (!value && default_value) {
              value = default_value;
            }
            
            if (value !== '') {
              url.searchParams.set(param_name, value);
            }
          });
        }
        
        // 3. SSO 토큰 추가
        if (dataSource.sso_config?.enabled) {
          try {
            const response = await axios.get('/api/users/current-token/', {
              withCredentials: true
            });
            const token = response.data.token;
            
            if (dataSource.sso_config.method === 'hash') {
              // 해시 방식: #param_name=token
              const paramName = dataSource.sso_config.param_name || 'auth_token';
              url.hash = `${paramName}=${token}`;
            } else {
              // 쿼리 방식: ?param_name=token
              const paramName = dataSource.sso_config.param_name || 'auth_token';
              url.searchParams.set(paramName, token);
            }
          } catch (error) {
            console.error('[WebFrame SSO] 토큰 조회 실패:', error);
            // 토큰 없이 진행
          }
        }
        
        const finalUrl = url.toString();
        console.log('[WebFrame URL Builder] URL 빌드 완료:', finalUrl);
        setBuiltWebframeUrl(finalUrl);
      } catch (error) {
        console.error('[WebFrame] URL 생성 실패:', dataSource.url, error);
        setBuiltWebframeUrl(dataSource.url);
      }
    };

    buildWebframeUrl();
  }, [component, getVariable, webframeUrlVariableValues]);

  useEffect(() => {
    if (component.subscribed_variables && onRefreshChartData) {
      // 마지막으로 적용한 변수 스냅샷을 컴포넌트별로 유지하여 중복 호출 방지
      const lastAppliedVarsRef = { current: getVariables(component.subscribed_variables) };

      // 변수 구독 설정
      const cleanup = subscribeToVariables(
        component.id,
        component.subscribed_variables,
        (changes) => {
          // 구독 변수가 변경되면 차트 데이터 새로고침 (값이 실제로 달라진 경우에만)
          // 주의: getVariables는 setState 타이밍상 직후에는 이전 값을 줄 수 있으므로
          // changes의 newValue로 오버레이하여 최신 값을 구성한다.
          const baseVars = lastAppliedVarsRef.current || getVariables(component.subscribed_variables);
          const currentVars = { ...baseVars };
          Object.keys(changes || {}).forEach(varName => {
            if (Object.prototype.hasOwnProperty.call(currentVars, varName)) {
              currentVars[varName] = changes[varName]?.newValue;
            }
          });

          const changed = (() => {
            if (!lastAppliedVarsRef.current) return true;
            const keys = Object.keys(component.subscribed_variables || {});
            for (const k of keys) {
              if (lastAppliedVarsRef.current[k] !== currentVars[k]) return true;
            }
            return false;
          })();
          if (changed) {
            lastAppliedVarsRef.current = { ...currentVars };
            onRefreshChartData(component.id, currentVars);
          }
        }
      );

      return cleanup;
    }
  }, [component.id, component.subscribed_variables, subscribeToVariables, getVariables, onRefreshChartData, debugMode]);

  // 향상된 차트 클릭 핸들러 - publish, openDashboard, callFunction 통합
  const handleChartClick = useCallback(async (clickedData, eventType) => {
    // on_click publish 처리 (대시보드 핸들러 호출보다 먼저 수행하여 발행 보장)
    let updates = {}; // updates를 상위 스코프에 선언
    if (component.on_click?.publish) {
      // 클릭 이벤트 정규화
      const chartType = component.chart_options?.viz_type || component.type;
      const $event = normalizeChartEvent(chartType, clickedData, componentData?.chart_data?.chart_options);
      
      // publish 정의 평가 및 변수 발행
      Object.entries(component.on_click.publish).forEach(([key, expression]) => {
        // 문자열 리터럴 자동 처리: 따옴표가 없고 $event/삼항/연산자가 아닌 경우 문자열로 간주
        let exprToEval = expression;
        if (typeof expression === 'string') {
          const isEventRef = expression.startsWith('$event.');
          const hasOperators = expression.includes('?') || expression.includes('>') || expression.includes('<') || expression.includes('+') || expression.includes('-') || expression.includes('*') || expression.includes('/');
          const isVarRef = expression.startsWith(':');
          const looksLikeNumber = /^-?\d+(\.\d+)?$/.test(expression.trim());
          const looksLikeBoolean = /^(true|false)$/i.test(expression.trim());
          if (!(isEventRef || hasOperators || isVarRef || looksLikeNumber || looksLikeBoolean)) {
            exprToEval = `'${String(expression).replace(/'/g, "\\'")}'`;
          }
        }
        const value = evaluateExpression(exprToEval, { $event });
        if (value !== null && value !== undefined) {
          updates[key] = value;
        }
      });
      
      if (debugMode) {
        try { console.log('[DashboardComponentRenderer] on_click.publish evaluated', { componentId: component.id, chartType, clickedData, $event, updates }); } catch (_) {}
      }
      
      if (Object.keys(updates).length > 0) {
        publishVariables(updates, component.id);
      } else {
      }
    }
    
    // on_click openDashboard 처리
    if (component.on_click?.openDashboard) {
      const dashboardConfig = component.on_click.openDashboard;
      
      // 현재 발행된 변수들과 전역 변수들을 팝업 대시보드에 전달
      const publishedVariables = component.on_click.publish ? updates : {};
      const globalVars = globalVariables?.variables || {};
      const currentVariables = { ...globalVars, ...publishedVariables };
      
      // 대시보드 팝업 열기 (멀티 탭 지원)
      openDashboard({
        // 멀티 탭 모드 지원
        dashboards: dashboardConfig.dashboards,
        // 단일 대시보드 모드 (하위 호환)
        dashboardId: dashboardConfig.dashboardId,
        dashboardName: dashboardConfig.dashboardName || dashboardConfig.title || `대시보드 상세`,
        title: dashboardConfig.title, // 멀티 탭용 제목
        // 크기 모드: 'auto'일 경우 대상 대시보드 컴포넌트 바운딩 박스에 맞춤
        sizeMode: dashboardConfig.sizeMode || 'fixed',
        size: dashboardConfig.size || { width: '80%', height: '70%' },
        position: dashboardConfig.position || 'center',
        variables: currentVariables,
        parentId: currentPopupId, // 현재 팝업 ID를 부모로 설정하여 부모-자식 관계 등록
        // 투명 대시보드 모드: 팝업 프레임 숨기고 차트만 표시
        transparentMode: dashboardConfig.transparentMode || false,
        options: {
          ...dashboardConfig.options,
          backdrop: {
            blur: dashboardConfig.backdrop?.blur !== false,
            closeOnClick: dashboardConfig.backdrop?.closeOnClick !== false,
            opacity: dashboardConfig.backdrop?.opacity || 0.7
          },
          animation: {
            type: dashboardConfig.animation?.type || 'fade',
            duration: dashboardConfig.animation?.duration || 300
          },
          resizable: dashboardConfig.resizable !== false,
          draggable: dashboardConfig.draggable !== false,
          closeOnEscape: dashboardConfig.closeOnEscape !== false,
          // 투명 모드 옵션도 options에 포함
          transparentMode: dashboardConfig.transparentMode || false
        },
        onClose: (popupId) => {
          // 필요시 팝업 닫힐 때 처리
        }
      });
    }

    // on_click callFunction 처리 (DB 함수 호출)
    if (component.on_click?.callFunction) {
      const { functionName, parameters, confirmMessage, successMessage, refreshAfter } = component.on_click.callFunction;
      // 단일 노트 설정 (legacy inputFields[0] → note로 승계)
      const legacyField = (Array.isArray(component.on_click?.callFunction?.inputFields) && component.on_click.callFunction.inputFields.length > 0) ? component.on_click.callFunction.inputFields[0] : null;
      const noteConfig = component.on_click?.callFunction?.note || (legacyField ? {
        label: legacyField.label || '처리 메모',
        placeholder: legacyField.placeholder || '',
        required: !!legacyField.required,
        default: legacyField.default || '',
        type: legacyField.type || 'textarea'
      } : null);
      let capturedInputValuesRef = {};

      try {
        const runCallFunction = async () => {
          // 파라미터 평가
          const evaluatedParams = {};
          const chartType = component.chart_options?.viz_type || component.type;
          const $event = normalizeChartEvent(chartType, clickedData, componentData?.chart_data?.chart_options);

          for (const [key, value] of Object.entries(parameters || {})) {
            if (typeof value === 'string') {
              if (value.startsWith('$event.')) {
                // 이벤트 데이터 참조
                const eventPath = value.substring(7);
                evaluatedParams[key] = evaluateExpression(`$event.${eventPath}`, { $event });
              } else if (value === '$input') {
                // 단일 입력값 (기본 id: 'input')
                evaluatedParams[key] = (capturedInputValuesRef && typeof capturedInputValuesRef === 'object') ? (capturedInputValuesRef['input'] ?? '') : '';
              } else if (value.startsWith('$input.')) {
                // 지정 입력 필드 참조
                const fieldId = value.substring(7);
                evaluatedParams[key] = (capturedInputValuesRef && typeof capturedInputValuesRef === 'object') ? (capturedInputValuesRef[fieldId] ?? '') : '';
              } else if (value.startsWith(':')) {
                // 구독 변수 참조 (단일 변수 안전 조회)
                const varName = value.substring(1);
                evaluatedParams[key] = getVariable(varName, null);
              } else {
                // 상수값
                evaluatedParams[key] = value;
              }
            } else {
              // 다른 타입은 그대로 전달
              evaluatedParams[key] = value;
            }
          }

          // 필수 파라미터 누락 방지: null/undefined이면 중단 (빈 문자열은 허용)
          const missingEntries = Object.entries(evaluatedParams)
            .filter(([, v]) => v === null || v === undefined)
            .map(([k]) => k);
          if (missingEntries.length > 0) {
            // 상세 가이드 메시지 구성
            const baseFn = (() => {
              try {
                const raw = String(functionName || '').trim();
                const noSig = raw.includes('(') ? raw.split('(')[0] : raw;
                return noSig;
              } catch { return functionName || ''; }
            })();

            const lines = [
              `DB 함수 호출 준비 실패`,
              `- 대상 함수: ${baseFn}`,
              `- 누락된 파라미터: ${missingEntries.join(', ')}`,
              ``,
              `원인 및 해결 방법:`
            ];

            missingEntries.forEach((p) => {
              const rawMap = parameters?.[p];
              if (typeof rawMap === 'string') {
                if (rawMap.startsWith(':')) {
                  const varName = rawMap.slice(1);
                  lines.push(`• ${p}: 구독 변수 ':${varName}'의 값이 설정되지 않았습니다(null/undefined). 변수 패널에서 ':${varName}' 값을 먼저 발행하거나, 파라미터를 상수로 지정하세요(예: 'active').`);
                } else if (rawMap.startsWith('$event.')) {
                  lines.push(`• ${p}: 이벤트 데이터 '${rawMap}'가 존재하지 않습니다. 클릭한 행/데이터에 해당 값이 있는지 확인하거나 상수/다른 변수로 매핑하세요.`);
                } else {
                  lines.push(`• ${p}: 상수 매핑이 설정되지 않았습니다. 예: 'active' 와 같이 값을 넣어주세요.`);
                }
              } else {
                lines.push(`• ${p}: 값이 설정되지 않았습니다(null/undefined). 적절한 상수 또는 변수로 매핑해 주세요.`);
              }
            });

            showErrorMessage(lines.join('<br>'));
            return;
          }

          if (debugMode) {
            console.log('[DashboardComponentRenderer] DB 함수 호출 준비', {
              functionName,
              parameters: evaluatedParams,
              componentId: component.id
            });
          }

          // DashboardService 임포트 필요
          const DashboardService = (await import('../services/DashboardService')).default;

          // DB 함수 실행
          const result = await DashboardService.executeDBFunction({
            functionName,
            parameters: evaluatedParams,
            schema: (functionName && functionName.includes('.') && !functionName.includes('(')) ? functionName.split('.')[0] : undefined
          });

          if (result.success) {
            // 성공 메시지 표시
            if (successMessage) {
              showSuccessMessage(successMessage);
            }

            // 차트 데이터 새로고침 (선택적)
            if (refreshAfter) {
              if (typeof onRefreshAllCharts === 'function') {
                onRefreshAllCharts();
              } else if (onRefreshChartData) {
                // 디듀프 회피용 nonce 파라미터 추가
                const nonceVars = { __refresh_ts: Date.now() };
                onRefreshChartData(component.id, nonceVars);
              }
            }

            // 자동 publish: 함수명 기반 네임스페이스로 결과 JSON 최상위 키를 발행
            try {
              const payload = {};
              const resultObj = result.result;
              const fnBase = (() => {
                try {
                  const raw = String(functionName || '').trim();
                  const noSig = raw.includes('(') ? raw.split('(')[0] : raw;
                  const parts = noSig.split('.');
                  return parts[parts.length - 1];
                } catch { return 'function'; }
              })();
              if (resultObj && typeof resultObj === 'object') {
                Object.entries(resultObj).forEach(([k, v]) => {
                  payload[`${fnBase}.${k}`] = v;
                });
              } else if (resultObj !== undefined) {
                payload[`${fnBase}.result`] = resultObj;
              }
              if (Object.keys(payload).length > 0) {
                publishVariables(payload, component.id);
                if (debugMode) {
                  console.log('[DashboardComponentRenderer] publish from function result', payload);
                }
              }
            } catch (_) {}

            if (debugMode) {
              console.log('[DashboardComponentRenderer] DB 함수 실행 성공', {
                functionName,
                result: result.result
              });
            }
          } else {
            // 오류 메시지 표시
            console.error('[DashboardComponentRenderer] DB 함수 실행 실패:', result.error);
            showErrorMessage(`함수 실행 실패: ${result.error || '알 수 없는 오류'}`);
          }
        };

        // 입력 플레이스홀더($input) 또는 inputFields가 있는지 확인
        const hasInputPlaceholder = Object.values(parameters || {}).some((v) => (
          typeof v === 'string' && (v === '$input' || v.startsWith('$input.'))
        ));

        const needsInput = hasInputPlaceholder || !!noteConfig;

        if (needsInput) {
          // 단일 note 필드로 구성
          const fields = [{
            id: 'note',
            label: (noteConfig && noteConfig.label) || '처리 메모',
            type: (noteConfig && noteConfig.type) || 'textarea',
            required: !!(noteConfig && noteConfig.required),
            placeholder: (noteConfig && noteConfig.placeholder) || '',
            default: (noteConfig && noteConfig.default) || ''
          }];

          const messageForPrompt = confirmMessage || '';
          showConfirmPrompt(messageForPrompt, fields, (values) => {
            capturedInputValuesRef = values || {};
            runCallFunction();
          }, () => {});
          return;
        }

        // 확인 메시지 표시 (입력 필요 없고 메시지만 있을 때)
        if (confirmMessage && !needsInput) {
          showConfirmMessage(confirmMessage, () => { runCallFunction(); }, () => {});
          return;
        }

        await runCallFunction();
      } catch (error) {
        console.error('[DashboardComponentRenderer] DB 함수 호출 중 오류:', error);
        showErrorMessage(`오류: ${error.message || '함수 호출 중 문제가 발생했습니다'}`);
      }
    }

    // 기존 클릭 핸들러 호출 (발행 및 팝업 처리 이후)
    if (onChartClick && isClickable) {
      try {
        onChartClick(component, clickedData, eventType);
      } catch (e) {
        if (debugMode) {
        }
      }
    }
  }, [
    onChartClick,
    component,
    isClickable,
    componentData,
    publishVariables,
    evaluateExpression,
    debugMode,
    openDashboard,
    getVariables,
    onRefreshChartData,
    onRefreshAllCharts,
    currentPopupId
  ]);

  if (!component || !component.id) {
    console.warn("[DashboardComponentRenderer] Invalid component provided", component);
    return <div key={component?.id || Math.random()} className="dashboard-component-invalid">Invalid Component</div>;
  }

  // 이미 위에서 선언됨
  
  // 프레임 표시 여부는 투명 옵션과 독립적으로 동작
  const showFrame = editMode ? true : originalShowFrame;
  
  // 디버깅용 로그 (성능을 위해 비활성화)
  // 활성 상태 확인
  const isActive = isActiveChart;
  
  
  // 드래그 상태 확인
  const isBeingDragged = isDragging && draggedComponentId === component.id;
  const isOtherBeingDragged = isDragging && draggedComponentId !== component.id;

  // 공통 컨테이너 스타일
  const containerStyle = (() => {
    let style = {};
    // transparentOption이 true 또는 "true"인 경우 투명 처리
    const isTransparent = transparentOption === true || transparentOption === 'true';
    const customBgColor = component?.chart_options?.options?.style?.background?.color;
    const backgroundColorStyle = isTransparent ? 'transparent' : (customBgColor || '#ffffff');

    // 모서리 둥글기 (기본값: 24px)
    const borderRadius = borderRadiusOption !== undefined ? `${borderRadiusOption}px` : '24px';

    // 편집 모드에서 originalShowFrame이 false인 경우 (점선 테두리만)
    if (editMode && !originalShowFrame) {
      style = {
        borderRadius: borderRadius,
        overflow: 'hidden',
        backgroundColor: backgroundColorStyle,
        border: '3px dashed rgba(74, 144, 226, 0.6)',
        boxShadow: 'none',
      };
    }
    // 일반 프레임 표시 (편집모드에서 originalShowFrame=true 또는 런타임에서 showFrame=true)
    else if (showFrame) {
      style = {
        borderRadius: borderRadius,
        overflow: 'hidden',
        backgroundColor: backgroundColorStyle,
        // 투명 모드일 때는 테두리와 그림자도 제거하여 완전 투명하게 처리 (사용자 기대사항 반영)
        border: isTransparent ? 'none' : '1px solid rgba(0, 0, 0, 0.08)',
        boxShadow: isTransparent ? 'none' : '0 20px 80px rgba(0, 0, 0, 0.25), 0 12px 48px rgba(0, 0, 0, 0.18), 0 6px 24px rgba(0, 0, 0, 0.12), 0 3px 12px rgba(0, 0, 0, 0.08)',
      };
    }
    // 런타임 모드에서 프레임 없음
    else {
      style = {
        borderRadius: borderRadius,
        backgroundColor: backgroundColorStyle,
        border: 'none',
        boxShadow: 'none',
      };
    }

    // 드래그 중인 컴포넌트는 최상위로
    if (isBeingDragged) {
      style.zIndex = 9999;
    }
    
    // 선택된 컴포넌트에 테두리 추가 (컴포넌트 자체 border로 표시해 가시성 유지)
    if (isSelected && editMode) {
      style.border = '3px solid #4a90e2';
    }
    
    // 그룹에 속한 컴포넌트는 개별 테두리 제거 (바운딩 박스로 대체)
    // 잠긴 그룹인 경우에만 배경 음영 표시
    if (editMode && component.group_id) {
      const group = dashboardTemplate?.groups?.find(g => g.id === component.group_id);
      
      if (group?.locked) {
        style.boxShadow = 'inset 0 0 0 2px rgba(255, 152, 0, 0.2)'; // 주황색 음영 (약하게)
      }
    }

    return style;
  })();

  
  // 편집 모드 컨트롤 버튼 (오버레이 형태로 표시)
  const EditControls = memo(() => (
    <div className="edit-controls-overlay">
      <button 
        className="edit-component-btn"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onComponentDoubleClick(component);
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        title="차트 수정"
        style={{
          pointerEvents: 'all',
          touchAction: 'manipulation'
        }}
      >
        ✎
      </button>
      <button 
        className="delete-component-btn"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (window.confirm('차트를 삭제하시겠습니까?\n삭제 후 저장하면 복원할 수 없습니다.')) {
            onDeleteComponent(component.id);
          }
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        title="차트 삭제"
        style={{
          pointerEvents: 'all',
          touchAction: 'manipulation'
        }}
      >
        ✕
      </button>
    </div>
  ));

  // 컨텍스트 메뉴 상태 (최상단에서 선언)
  // const [contextMenu, setContextMenu] = React.useState({ visible: false, x: 0, y: 0 }); // 이미 위에서 선언됨

  // 우클릭 핸들러
  const handleContextMenu = (e) => {
    if (!editMode) return;
    e.preventDefault();
    e.stopPropagation();
    // setContextMenu({ visible: true, x: e.clientX, y: e.clientY }); // 내부 상태 제거
  };

  // 메뉴 닫기
  // const closeContextMenu = () => setContextMenu({ ...contextMenu, visible: false }); // 내부 상태 제거

  // z-순서 메뉴 항목
  // const zOrderMenuItems = [ // 이미 위에서 선언됨
  //   { label: '맨 앞으로 보내기', onClick: () => onZOrderAction(component.id, 'bringToFront') },
  //   { label: '앞으로 보내기', onClick: () => onZOrderAction(component.id, 'bringForward') },
  //   { label: '뒤로 보내기', onClick: () => onZOrderAction(component.id, 'sendBackward') },
  //   { label: '맨 뒤로 보내기', onClick: () => onZOrderAction(component.id, 'sendToBack') },
  // ];

  // 컴포넌트 데이터가 없는 경우 (로딩 중)
  if (!componentData) {
    // webframe 타입의 경우 URL 정보로 즉시 렌더링
    // URL 추출 로직: 직접 구조와 중첩 구조 모두 지원
    const extractWebframeUrl = (dataSource) => {
      if (!dataSource) return null;
      // 직접 구조: data_source.url
      if (dataSource.url) return dataSource.url;
      // 중첩 구조: data_source.data.url
      if (dataSource.data && dataSource.data.url) return dataSource.data.url;
      return null;
    };
    
    // SSO, 쿼리 파라미터, 또는 URL 변수 패턴이 있는 경우 builtWebframeUrl을 기다림
    const hasUrlVariables = /\{:\w+\}/.test(component?.data_source?.url || '');
    const needsBuilding = hasUrlVariables ||
                          component?.data_source?.sso_config?.enabled || 
                          (component?.data_source?.query_params && component.data_source.query_params.length > 0);
    
    // 웹프레임 URL 결정: builtWebframeUrl 우선 사용 (SSO/쿼리파라미터 포함)
    const webframeUrl = builtWebframeUrl || extractWebframeUrl(component?.data_source);
    
    // SSO/쿼리파라미터가 필요한데 아직 빌드 안 됐으면 로딩 또는 안내 메시지 표시
    if (isWebframeChart && needsBuilding && !builtWebframeUrl) {
      // 설정되지 않은 변수가 있는 경우 안내 메시지 표시
      const hasMissingVars = missingVariables && missingVariables.length > 0;
      return (
        <div 
          key={component.id} 
          data-component-id={component.id}
          className={`dashboard-component ${!originalShowFrame ? 'frameless-chart' : ''}`}
          style={containerStyle}
        >
          {editMode && <EditControls />}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: hasMissingVars ? '#888' : '#666'
          }}>
            <div style={{ textAlign: 'center' }}>
              {hasMissingVars ? (
                <>
                  <div style={{ fontSize: '24px', marginBottom: '10px' }}>ℹ️</div>
                  <div>{missingVariables.join(', ')}가 선택되어 있지 않습니다</div>
                </>
              ) : (
                <>
                  <div className="spinner-border spinner-border-sm" role="status" style={{ marginBottom: '10px' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div>URL 생성 중...</div>
                </>
              )}
            </div>
          </div>
        </div>
      );
    }
    
    if (isWebframeChart && webframeUrl) {
      return (
        <div 
          key={component.id} 
          data-component-id={component.id}
          className={`dashboard-component ${!originalShowFrame ? 'frameless-chart' : ''} ${isActiveChart || isClicked ? 'chart-clicked' : ''} ${isSelected && editMode ? 'selected-component' : ''}`}
          style={containerStyle}
          onMouseDownCapture={handleMouseDownCapture}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onClick={handleClick}
          onDoubleClickCapture={handleDoubleClickCapture}
        >
          {editMode && <EditControls />}
          {!editMode && shouldShowInfoIcon(component, componentData?.lastUpdated) &&
            <div className="component-info-icon" title={getInfoTooltipText(component.description, componentData?.lastUpdated)}>ⓘ</div>
          }
          <DynamicChart 
            key={`${component.id}_${builtWebframeUrl || webframeUrl}`}  // builtWebframeUrl 변경 시 iframe 강제 리렌더링
            chartData={{
              viz_type: 'webframe',
              url: webframeUrl,  // 표준 위치에 URL 설정
              options: {
                title: processTitleWithVariables(component.title) || component.chart_options?.title,
                showFrame: originalShowFrame,
                ...(() => {
                  const opts = component.chart_options?.options || {};
                  // advanced에서 showDescriptionTooltip 제거
                  if (opts.advanced) {
                    const { showDescriptionTooltip, ...cleanAdvanced } = opts.advanced;
                    return { ...opts, advanced: cleanAdvanced };
                  }
                  return opts;
                })()
              }
            }} 
            className="dashboard-chart"
            style={{ 
              width: '100%', 
              height: '100%'
            }}
            forceResize={isOtherBeingDragged ? 0 : resizeCounter}
            isEditMode={editMode}
            isActiveChart={isActiveChart}
            isClicked={isClicked}
            isSelected={!!isSelected}
            componentId={component.id}
            onEmergencyTrigger={triggerEmergency}
            onChartDoubleClick={handleChartDoubleClick}
            onChartClick={!editMode && isClickable ? handleChartClick : null}
          />
        </div>
      );
    }
    
    // DatePicker 타입의 경우 즉시 렌더링 (SQL 쿼리 없이)
    if (isDatePickerChart) {
      return (
        <div 
          key={component.id} 
          data-component-id={component.id}
          className={`dashboard-component ${!originalShowFrame ? 'frameless-chart' : ''} ${isActiveChart || isClicked ? 'chart-clicked' : ''} ${isSelected && editMode ? 'selected-component' : ''}`}
          style={containerStyle}
          onMouseDownCapture={handleMouseDownCapture}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onClick={handleClick}
          onDoubleClickCapture={handleDoubleClickCapture}
        >
          {editMode && <EditControls />}
          {!editMode && shouldShowInfoIcon(component, componentData?.lastUpdated) &&
            <div className="component-info-icon" title={getInfoTooltipText(component.description, componentData?.lastUpdated)}>ⓘ</div>
          }
          <DynamicChart 
            chartData={{
              viz_type: 'date_picker',
              options: {
                // 기존 옵션 병합 (먼저 스프레드)
                ...(component.chart_options?.options || component.options || {}),
                // 공통 옵션 (나중에 덮어쓰기)
                title: processTitleWithVariables(component.title) || component.chart_options?.title,
                showTitle: component.chart_options?.options?.style?.visibility?.title !== false,  // visibility.title 매핑 추가
                showFrame: originalShowFrame,
                transparent: component.chart_options?.transparent,
                // advanced 옵션 병합
                advanced: {
                  // 기본값
                  mode: 'range',
                  dateFormat: 'yyyy-MM-dd',
                  showTime: false,
                  defaultRange: 'last_7_days',
                  showQuickButtons: true,
                  // component의 advanced 옵션으로 덮어쓰기 (showDescriptionTooltip 제외)
                  ...(() => {
                    const { showDescriptionTooltip, ...cleanAdvanced } = (component.style?.options?.advanced || component.chart_options?.options?.advanced || {});
                    return cleanAdvanced;
                  })()
                },
                // style 옵션 병합 (visibility 제외 - showTitle로 이미 처리됨)
                style: {
                  ...(() => {
                    const { visibility, ...styleWithoutVisibility } = component.chart_options?.options?.style || {};
                    return styleWithoutVisibility;
                  })()
                }
              }
            }} 
            className="dashboard-chart"
            style={{ 
              width: '100%', 
              height: '100%'
            }}
            forceResize={isOtherBeingDragged ? 0 : resizeCounter}
            isEditMode={editMode}
            isActiveChart={isActiveChart}
            isClicked={isClicked}
            isSelected={!!isSelected}
            componentId={component.id}
            onEmergencyTrigger={triggerEmergency}
            onChartDoubleClick={handleChartDoubleClick}
            onChartClick={!editMode && isClickable ? handleChartClick : null}
          />
        </div>
      );
    }
    
    // 배지 타입의 경우 즉시 렌더링 (로딩 상태로)
    if (isBadgeChart) {
      return (
        <div 
          key={component.id} 
          className={`dashboard-component ${!originalShowFrame ? 'frameless-chart' : ''} ${isActiveChart || isClicked ? 'chart-clicked' : ''} ${isClickableRuntime ? 'clickable-chart' : ''} ${isSelected && editMode ? 'selected-component' : ''}`}
          style={containerStyle}
          onMouseDownCapture={handleMouseDownCapture}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onClick={handleClick}
          onDoubleClickCapture={handleDoubleClickCapture}
        >
          {editMode && <EditControls />}
          {!editMode && shouldShowInfoIcon(component, componentData?.lastUpdated) &&
            <div className="component-info-icon" title={getInfoTooltipText(component.description, componentData?.lastUpdated)}>ⓘ</div>
          }
          <DynamicChart 
            chartData={{
              viz_type: 'badge',
              options: {
                title: processTitleWithVariables(component.title) || component.chart_options?.title,
                showFrame: originalShowFrame,
                ...(() => {
                  const opts = component.chart_options?.options || {};
                  // advanced에서 showDescriptionTooltip 제거
                  if (opts.advanced) {
                    const { showDescriptionTooltip, ...cleanAdvanced } = opts.advanced;
                    return { ...opts, advanced: cleanAdvanced };
                  }
                  return opts;
                })(),
                // transparent 옵션을 즉시 렌더 경로에도 전달
                transparent: component.chart_options?.transparent
              },
              data: null, // 로딩 중임을 나타냄
              isLoading: true
            }} 
            className="dashboard-chart"
            style={{ 
              width: '100%', 
              height: '100%'
            }}
            forceResize={isOtherBeingDragged ? 0 : resizeCounter}
            isEditMode={editMode}
            isActiveChart={isActiveChart}
            isClicked={isClicked}
            isSelected={!!isSelected}
            componentId={component.id}
            onEmergencyTrigger={triggerEmergency}
            onChartDoubleClick={handleChartDoubleClick}
            onChartClick={!editMode && isClickable ? handleChartClick : null}
          />
        </div>
      );
    }
    
    // 다른 차트 타입은 기존 스켈레톤 표시
    return (
      <div 
        key={component.id} 
        data-component-id={component.id}
        className={`dashboard-component ${!originalShowFrame ? 'frameless-chart' : ''} component-loading ${isClicked ? 'chart-clicked' : ''} ${isSelected && editMode ? 'selected-component' : ''}`}
        style={containerStyle}
        onMouseDownCapture={handleMouseDownCapture}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
          onClick={handleClick}
        onDoubleClickCapture={handleDoubleClickCapture}
      >
        {editMode && <EditControls />}
        {!editMode && shouldShowInfoIcon(component, componentData?.lastUpdated) &&
          <div className="component-info-icon" title={getInfoTooltipText(component.description, componentData?.lastUpdated)}>ⓘ</div>
        }
        
        {/* 차트 타입 스켈레톤 */}
        <div className="individual-skeleton-chart">
          <div className="skeleton-chart-header">
            <div className="skeleton-shimmer skeleton-text-medium"></div>
          </div>
          <div className="skeleton-chart-body">
            <div className="skeleton-shimmer skeleton-chart-area"></div>
          </div>
        </div>
      </div>
    );
  }
  
  if (componentData.error) {
    let errorIcon = '⚠️';
    let errorMessageText = '데이터를 불러올 수 없습니다';
    
    if (componentData.errorType === 'server') {
      errorIcon = '🔧';
      errorMessageText = '서버 오류가 발생했습니다';
    } else if (componentData.errorType === 'invalid_query') {
      errorIcon = '❗';
      errorMessageText = '쿼리가 올바르지 않습니다';
    } else if (componentData.errorType === 'api_error') {
      errorIcon = '📊';
      errorMessageText = '차트 데이터 처리 중 오류';
    }
    
    return (
      <div 
        key={component.id} 
        data-component-id={component.id}
        className={`dashboard-component-error ${!originalShowFrame ? 'frameless-chart' : ''} ${isActiveChart || isClicked ? 'chart-clicked' : ''}`}
        style={containerStyle}
        onMouseDownCapture={handleMouseDownCapture}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
          onClick={handleClick}
        onDoubleClickCapture={handleDoubleClickCapture}
      >
        <OnRenderComplete callback={componentData?.onRenderComplete} />
        {editMode && <EditControls />}
        {!editMode && shouldShowInfoIcon(component, componentData?.lastUpdated) &&
          <div className="component-info-icon" title={getInfoTooltipText(component.description, componentData?.lastUpdated)}>ⓘ</div>
        }
        <div className="error-message">
          <div className="error-icon">{errorIcon}</div>
          <p>{errorMessageText}</p>
          <p className="error-details">{componentData.errorMessage}</p>
          <button 
            className="retry-button" 
            onClick={() => onRefreshChartData(component)}
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }
  
  // 텍스트 라벨 처리 (static과 sql 모두 여기서 처리)
  const isTextLabel = component.chart_options?.viz_type === 'text_label';
  if (isTextLabel) {
    // 편집 중인 텍스트 라벨의 경우 미리보기 데이터를 우선 사용
    const activeComponent = (textLabelPreviewData && textLabelPreviewData.id === component.id) 
      ? textLabelPreviewData 
      : component;
    
    const activeOptions = activeComponent.chart_options?.options || {};
    
    // 미리보기 데이터가 있을 때는 activeOptions.content를 우선 사용
    let activeContent = '';
    
    // SQL 타입인 경우 쿼리 결과에서 첫 번째 값 추출
    if (component.data_source?.type === 'sql') {
        // 로딩 중인 경우
        if (!componentData) {
          activeContent = '로딩 중...';
        } else if (componentData?.error) {
          // 에러가 있는 경우
          activeContent = componentData.errorMessage || 'SQL 쿼리 실행 오류';
        } else if (componentData?.chart_data || componentData?.raw_data) {
          // 백엔드에서 처리한 text_value 먼저 확인
          const textValue = componentData.chart_data?.chart_options?.data?.text_value;
          if (textValue !== undefined) {
            activeContent = String(textValue);
          } else {
            // text_value가 없으면 기존 방식으로 raw_data에서 추출
            const rawData = componentData.raw_data || componentData.query_result || componentData.chart_data?.chart_options?.data?.raw_data;
            if (rawData && rawData.length > 0) {
              // 첫 번째 행의 첫 번째 컬럼 값 추출
              const firstRow = rawData[0];
              const firstValue = Object.values(firstRow)[0];
              activeContent = String(firstValue !== null && firstValue !== undefined ? firstValue : '');
            } else {
              // chart_data에서 추출 시도 (텍스트 라벨용 특별 처리)
              // 백엔드가 텍스트 라벨용으로 단순 값을 반환하는 경우
              if (componentData.chart_data?.value !== undefined) {
                activeContent = String(componentData.chart_data.value);
              } else if (componentData.value !== undefined) {
                activeContent = String(componentData.value);
              } else {
                // 텍스트 라벨용 chart_data 구조 확인
                const chartOptions = componentData.chart_data?.chart_options;
                // 텍스트 라벨의 경우 값이 title에 있을 수 있음
                if (chartOptions?.title) {
                  activeContent = String(chartOptions.title);
                } else {
                  // 일반 차트 데이터 구조에서 추출
                  const chartData = chartOptions?.data;
                  if (chartData?.series && chartData.series.length > 0 && chartData.series[0].values && chartData.series[0].values.length > 0) {
                    // 첫 번째 시리즈의 첫 번째 값을 표시
                    activeContent = String(chartData.series[0].values[0]);
                  } else if (chartData?.categories && chartData.categories.length > 0) {
                    // series가 없으면 categories의 첫 번째 값을 표시
                    activeContent = String(chartData.categories[0]);
                  } else {
                    activeContent = '데이터 없음';
                  }
                }
              }
            }
          }
        } else {
          activeContent = '데이터 없음';
        }
      } else {
        // static 타입이거나 미리보기인 경우
        activeContent = (textLabelPreviewData && textLabelPreviewData.id === component.id)
          ? activeOptions.content || '텍스트를 입력하세요'
          : componentData?.content || activeOptions.content || '텍스트를 입력하세요';
      }
      
      // 정렬 옵션 처리
      const verticalAlign = activeOptions.verticalAlign || 'center';
      const horizontalAlign = activeOptions.textAlign || 'center';
      
      // flexbox 정렬 값 변환
      const justifyContent = (() => {
        switch(horizontalAlign) {
          case 'left': return 'flex-start';
          case 'right': return 'flex-end';
          case 'center':
          default: return 'center';
        }
      })();
      
      const alignItems = (() => {
        switch(verticalAlign) {
          case 'flex-start': return 'flex-start';
          case 'flex-end': return 'flex-end';
          case 'center':
          default: return 'center';
        }
      })();
      
      // 단순한 텍스트 스타일
      const textStyle = {
        fontSize: (activeOptions.fontSize || activeOptions.baseFontSize || '35').toString().replace('px', '') + 'px',
        fontWeight: activeOptions.fontWeight || 'normal',
        fontStyle: activeOptions.fontStyle || 'normal',
        color: activeOptions.textColor || '#333333',
        textAlign: horizontalAlign,
        fontFamily: activeOptions.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        lineHeight: activeOptions.lineHeight || '1.4',
        borderRadius: activeOptions.borderRadius || '0px',
        wordBreak: 'break-word',
        whiteSpace: 'pre-wrap',
        textShadow: activeOptions.textShadow && activeOptions.textShadow !== 'none' ? activeOptions.textShadow : 'none',
        textDecoration: activeOptions.textDecoration || 'none',
        margin: 0,
        padding: '10px',
        boxSizing: 'border-box'
      };
      
      // 컨테이너 스타일
      // transparent 플래그를 우선 적용하여 배경을 결정 (flat/background 상관없이)
      const effectiveBackgroundColor = (activeComponent.chart_options?.transparent === true || activeComponent.chart_options?.transparent === 'true')
        ? 'transparent'
        : (activeOptions.backgroundColor || activeOptions?.style?.background?.color || 'transparent');

      const textContainerStyle = {
        width: '100%', 
        height: '100%',
        display: 'flex',
        justifyContent: justifyContent,
        alignItems: alignItems,
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: effectiveBackgroundColor
      };
      
      return (
        <div 
          key={component.id} 
          data-component-id={component.id}
          className={`dashboard-component ${!originalShowFrame ? 'frameless-chart' : ''} ${isActiveChart || isClicked ? 'chart-clicked' : ''} ${isSelected && editMode ? 'selected-component' : ''}`}
          style={containerStyle}
          onMouseDownCapture={handleMouseDownCapture}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onClick={handleClick}
          onContextMenu={onChartContextMenu}
          onDoubleClickCapture={handleDoubleClickCapture}
        >
          <OnRenderComplete callback={componentData?.onRenderComplete} />
          <div style={textContainerStyle}>
            <div style={textStyle}>
              {processContentWithVariables(activeContent)}
            </div>
          </div>
          
          {/* 편집 모드에서는 컨트롤을 오버레이 형태로 표시 */}
          {editMode && <EditControls />}
          {/* 편집 모드가 아닐 때만 description icon 표시 */}
          {!editMode && shouldShowInfoIcon(component, componentData?.lastUpdated) && (
            <div className="component-info-icon" title={getInfoTooltipText(component.description, componentData?.lastUpdated)}>ⓘ</div>
          )}
          {/* 컨텍스트 메뉴 */}
          <ContextMenu
            visible={showContextMenu}
            x={contextMenuX}
            y={contextMenuY}
            items={zOrderMenuItems}
            onClose={closeContextMenu}
          />
        </div>
      );
  }
  
  // DatePicker 타입의 경우 static이어도 특별 처리
  if (isDatePickerChart) {
    return (
      <div 
        key={component.id} 
        data-component-id={component.id}
        data-viz-type="date_picker"
        className={`dashboard-component date-picker-component ${!originalShowFrame ? 'frameless-chart' : ''} ${isActiveChart || isClicked ? 'chart-clicked' : ''} ${isSelected && editMode ? 'selected-component' : ''}`}
        style={containerStyle}
        onMouseDownCapture={handleMouseDownCapture}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
          onClick={handleClick}
        onContextMenu={onChartContextMenu}
        onDoubleClickCapture={handleDoubleClickCapture}
      >
        {editMode && <EditControls />}
        {!editMode && shouldShowInfoIcon(component, componentData?.lastUpdated) &&
          <div className="component-info-icon" title={getInfoTooltipText(component.description, componentData?.lastUpdated)}>ⓘ</div>
        }
        <DynamicChart 
          chartData={{
            viz_type: 'date_picker',
            options: {
              // 기존 옵션 병합 (먼저 스프레드)
              ...(component.chart_options?.options || component.options || {}),
              // 공통 옵션 (나중에 덮어쓰기)
              title: processTitleWithVariables(component.title) || component.chart_options?.title,
              showTitle: component.chart_options?.options?.style?.visibility?.title !== false,  // visibility.title 매핑 추가
              showFrame: component.chart_options?.showFrame !== undefined ? component.chart_options.showFrame : false,
              transparent: component.chart_options?.transparent !== undefined ? component.chart_options.transparent : true,
              // advanced 옵션 병합
              advanced: {
                // 기본값
                mode: 'range',
                dateFormat: 'yyyy-MM-dd',
                showTime: false,
                defaultRange: 'last_7_days',
                showQuickButtons: true,
                // component의 advanced 옵션으로 덮어쓰기 (showDescriptionTooltip 제외)
                ...(() => {
                  const { showDescriptionTooltip, ...cleanAdvanced } = (component.chart_options?.options?.advanced || {});
                  return cleanAdvanced;
                })()
              },
              // style 옵션 병합 (visibility 제외 - showTitle로 이미 처리됨)
              style: {
                ...(() => {
                  const { visibility, ...styleWithoutVisibility } = component.chart_options?.options?.style || {};
                  return styleWithoutVisibility;
                })()
              }
            }
          }} 
          className="dashboard-chart"
          style={{ 
            width: '100%', 
            height: '100%'
          }}
          forceResize={isOtherBeingDragged ? 0 : resizeCounter}
          isEditMode={editMode}
          isActiveChart={isActiveChart}
          isClicked={isClicked}
          componentId={component.id}
          onEmergencyTrigger={triggerEmergency}
          onChartDoubleClick={handleChartDoubleClick}
          onChartClick={!editMode && isClickable ? handleChartClick : null}
        />
        {/* 컨텍스트 메뉴 */}
        <ContextMenu
          visible={showContextMenu}
          x={contextMenuX}
          y={contextMenuY}
          items={zOrderMenuItems}
          onClose={closeContextMenu}
        />
      </div>
    );
  }
  
  // 기존 static 타입 처리 (markdown 등)
  if (componentData.type === 'static' && component.chart_options?.viz_type !== 'webframe') {
    const isMarkdown = component.chart_options?.viz_type === 'markdown';
    const simpleMarkdown = (text) => text.replace(/\n/g, '<br/>');
    
    return (
      <div 
        key={component.id} 
        data-component-id={component.id}
        className={`dashboard-component static-content ${!originalShowFrame ? 'frameless-chart' : ''} ${isMarkdown ? 'markdown-content' : ''} ${isActiveChart || isClicked ? 'chart-clicked' : ''} ${isClickableRuntime ? 'clickable-chart' : ''} ${isSelected && editMode ? 'selected-component' : ''}`}
        style={containerStyle}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
          onClick={handleClick}
      >
        <OnRenderComplete callback={componentData?.onRenderComplete} />
        {editMode && <EditControls />}
        {!editMode && shouldShowInfoIcon(component, componentData?.lastUpdated) &&
          <div className="component-info-icon" title={getInfoTooltipText(component.description, componentData?.lastUpdated)}>ⓘ</div>
        }
        <div 
          className="static-text"
          style={{ height: '100%' }}
          dangerouslySetInnerHTML={{ 
            __html: isMarkdown ? simpleMarkdown(componentData.content) : componentData.content
          }}
        />
      </div>
    );
  }
  
  // 빈 데이터 체크 (webframe 타입은 제외)
  if (componentData.chart_data && 
      componentData.chart_data.chart_options?.viz_type !== 'webframe' &&
      ((componentData.chart_data.chart_options?.data?.categories && componentData.chart_data.chart_options.data.categories.length === 0) || 
       (componentData.chart_data.chart_options?.data?.series && componentData.chart_data.chart_options.data.series.every(s => !s.values || s.values.length === 0)))) {
    return (
      <div 
        key={component.id} 
        data-component-id={component.id}
        className={`dashboard-component-empty ${isActiveChart || isClicked ? 'chart-clicked' : ''} ${isSelected && editMode ? 'selected-component' : ''}`}
        style={containerStyle}
        onMouseDownCapture={handleMouseDownCapture}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
          onClick={handleClick}
        onDoubleClickCapture={handleDoubleClickCapture}
      >
        <OnRenderComplete callback={componentData?.onRenderComplete} />
        {editMode && <EditControls />}
        {!editMode && shouldShowInfoIcon(component, componentData?.lastUpdated) &&
          <div className="component-info-icon" title={getInfoTooltipText(component.description, componentData?.lastUpdated)}>ⓘ</div>
        }
        <div className="empty-data-message">
          <p>해당기간에 데이터가 없습니다.</p>
          <p className="empty-data-details">실제 데이터가 존재하지 않거나 분석 모델 학습이 진행중입니다.</p>
        </div>
      </div>
    );
  }
  
  // chartProps가 null이면 로딩 중이거나 에러 상태
  if (!chartProps) {
    return null; // 이미 위에서 처리됨
  }

  // webframe인 경우 builtWebframeUrl이 준비될 때까지 로딩 표시 (componentData가 있는 경우에도 적용)
  const isWebframeWithVariables = isWebframeChart && /\{:\w+\}/.test(component?.data_source?.url || '');
  const webframeNeedsBuilding = isWebframeWithVariables ||
                                 (isWebframeChart && component?.data_source?.sso_config?.enabled) ||
                                 (isWebframeChart && component?.data_source?.query_params?.length > 0);
  
  if (webframeNeedsBuilding && !builtWebframeUrl) {
    // 설정되지 않은 변수가 있는 경우 안내 메시지 표시
    const hasMissingVars = missingVariables && missingVariables.length > 0;
    return (
      <div 
        key={component.id} 
        data-component-id={component.id}
        className={`dashboard-component ${!originalShowFrame ? 'frameless-chart' : ''}`}
        style={containerStyle}
      >
        {editMode && <EditControls />}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: hasMissingVars ? '#888' : '#666'
        }}>
          <div style={{ textAlign: 'center' }}>
            {hasMissingVars ? (
              <>
                <div style={{ fontSize: '24px', marginBottom: '10px' }}>ℹ️</div>
                <div>{missingVariables.join(', ')}가 선택되어 있지 않습니다</div>
              </>
            ) : (
              <>
                <div className="spinner-border spinner-border-sm" role="status" style={{ marginBottom: '10px' }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div>URL 생성 중...</div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      key={component.id} 
      data-component-id={component.id}
      className={`dashboard-component ${!originalShowFrame ? 'frameless-chart' : ''} ${isActiveChart || isClicked ? 'chart-clicked' : ''} ${isClickableRuntime ? 'clickable-chart' : ''} ${isSelected && editMode ? 'selected-component' : ''}`}
      style={containerStyle}
      onMouseDownCapture={handleMouseDownCapture}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
          onClick={handleClick}
      onClick={handleClick}
      onContextMenu={onChartContextMenu}
      onDoubleClickCapture={handleDoubleClickCapture}
      // onClick 제거 - onMouseDown에서 처리
    >
      {editMode && <EditControls />}
      {!editMode && shouldShowInfoIcon(component, componentData?.lastUpdated) &&
        <div className="component-info-icon" title={getInfoTooltipText(component.description, componentData?.lastUpdated)}>ⓘ</div>
      }
      <DynamicChart
        chartData={chartProps}
        className="dashboard-chart"
        style={{
          width: '100%',
          height: '100%'
        }}
        forceResize={isOtherBeingDragged ? 0 : resizeCounter}
        isEditMode={editMode}
        printMode={printMode}
        resizeDebounceMsOverride={isOtherBeingDragged ? 180 : undefined}
        isActiveChart={isActiveChart}
        isClicked={isClicked}
        isSelected={!!isSelected}
        componentId={component.id}
        onEmergencyTrigger={triggerEmergency}
        onChartDoubleClick={handleChartDoubleClick}
        onChartClick={!editMode && isClickable ? handleChartClick : null}
        onRenderComplete={componentData?.onRenderComplete}
      />
      {/* 컨텍스트 메뉴 */}
      <ContextMenu
        visible={showContextMenu}
        x={contextMenuX}
        y={contextMenuY}
        items={zOrderMenuItems}
        onClose={closeContextMenu}
      />
    </div>
  );
}, (prevProps, nextProps) => {
  // textLabelPreviewData 변경은 해당 컴포넌트에만 영향을 미쳐야 함
  if (prevProps.component.id !== nextProps.component.id) {
    return false; // ID가 다르면 리렌더링
  }
  
  const componentId = prevProps.component.id;
  
  // 라인차트의 경우 함수 참조 변경 추적
  if (prevProps.component.chart_options?.viz_type === 'line') {
  }
  
  
  // 텍스트 라벨이 아닌 경우 textLabelPreviewData 변경 무시
  const isTextLabel = prevProps.component.chart_options?.viz_type === 'text_label';
  if (!isTextLabel) {
    // textLabelPreviewData를 제외한 다른 props 비교
    const propsEqual = (
      prevProps.editMode === nextProps.editMode &&
      prevProps.isDragging === nextProps.isDragging &&
      prevProps.draggedComponentId === nextProps.draggedComponentId &&
      prevProps.resizeCounter === nextProps.resizeCounter &&
      prevProps.onComponentDoubleClick === nextProps.onComponentDoubleClick &&
      prevProps.onDeleteComponent === nextProps.onDeleteComponent &&
      prevProps.onRefreshChartData === nextProps.onRefreshChartData &&
      prevProps.onChartClick === nextProps.onChartClick &&
      prevProps.isActiveChart === nextProps.isActiveChart &&
      prevProps.isClicked === nextProps.isClicked &&
      prevProps.dashboardTemplate === nextProps.dashboardTemplate
    );
    
    // componentData의 차트 데이터 비교 (더 정확한 비교)
    const prevChartData = prevProps.componentData?.chart_data?.chart_options;
    const nextChartData = nextProps.componentData?.chart_data?.chart_options;
    
    const chartDataEqual = (
      prevChartData?.viz_type === nextChartData?.viz_type &&
      prevChartData?.title === nextChartData?.title &&
      JSON.stringify(prevChartData?.data) === JSON.stringify(nextChartData?.data) &&
      JSON.stringify(prevChartData?.options) === JSON.stringify(nextChartData?.options) &&
      prevProps.componentData?.lastUpdated === nextProps.componentData?.lastUpdated &&
      // 템플릿(로컬) 옵션/제목/표시옵션이 바뀌면 반드시 리렌더링
      JSON.stringify(prevProps.component?.chart_options?.options || {}) === JSON.stringify(nextProps.component?.chart_options?.options || {}) &&
      (prevProps.component?.title || '') === (nextProps.component?.title || '') &&
      prevProps.component?.chart_options?.transparent === nextProps.component?.chart_options?.transparent &&
      prevProps.component?.chart_options?.showFrame === nextProps.component?.chart_options?.showFrame
    );
    
    return propsEqual && chartDataEqual;
  }
  
  // 텍스트 라벨인 경우, 자신의 미리보기 데이터만 확인
  const prevIsPreview = prevProps.textLabelPreviewData?.id === prevProps.component.id;
  const nextIsPreview = nextProps.textLabelPreviewData?.id === nextProps.component.id;
  
  if (prevIsPreview || nextIsPreview) {
    return false; // 미리보기 상태가 변경되면 리렌더링
  }
  
  // componentData deep comparison for non-chart data
  const componentDataEqual = (() => {
    if (prevProps.componentData === nextProps.componentData) return true;
    if (!prevProps.componentData || !nextProps.componentData) return false;
    
    // Compare error states
    if (prevProps.componentData.error !== nextProps.componentData.error) return false;
    if (prevProps.componentData.errorType !== nextProps.componentData.errorType) return false;
    if (prevProps.componentData.errorMessage !== nextProps.componentData.errorMessage) return false;
    
    // Compare static content
    if (prevProps.componentData.type === 'static' && nextProps.componentData.type === 'static') {
      return prevProps.componentData.content === nextProps.componentData.content &&
             prevProps.componentData.lastUpdated === nextProps.componentData.lastUpdated;
    }
    
    // For other types, compare by lastUpdated timestamp
    return prevProps.componentData.lastUpdated === nextProps.componentData.lastUpdated;
  })();
  
  // 나머지 props 비교 (showContextMenu 관련 props 추가)
  const areEqual = (
    componentDataEqual &&
    prevProps.editMode === nextProps.editMode &&
    prevProps.isDragging === nextProps.isDragging &&
    prevProps.draggedComponentId === nextProps.draggedComponentId &&
    prevProps.resizeCounter === nextProps.resizeCounter &&
    prevProps.onComponentDoubleClick === nextProps.onComponentDoubleClick &&
    prevProps.onDeleteComponent === nextProps.onDeleteComponent &&
    prevProps.onRefreshChartData === nextProps.onRefreshChartData &&
    prevProps.onChartClick === nextProps.onChartClick &&
    prevProps.dashboardTemplate === nextProps.dashboardTemplate &&
    prevProps.activeChartState?.[prevProps.component.id] === nextProps.activeChartState?.[nextProps.component.id] &&
    prevProps.isClicked === nextProps.isClicked &&
    prevProps.showContextMenu === nextProps.showContextMenu &&
    prevProps.contextMenuX === nextProps.contextMenuX &&
    prevProps.contextMenuY === nextProps.contextMenuY
  );
  
  return areEqual;
});

DashboardComponentRenderer.displayName = 'DashboardComponentRenderer';

export default DashboardComponentRenderer; 
